/**
 * Supabase query functions for the unified config tables.
 * Uses the existing getSupabase() singleton and retry patterns.
 */

import { getSupabase } from '../../services/supabase';
import { logger } from '../../utils/core/logger';
import { isSupabaseRequestCancelled } from '../../utils/core/supabaseCancelDetection';
import type {
    ActiveDomainExperiment,
    ActiveStaffExperiment,
} from '../../utils/widgetInit/experimentCohorts';
import { type DomainMatchStrategy, getDomainCandidates, selectDomainMatch } from './domainMatching';
import type { Client, ClientConfigRow, ConfigDefinition, Domain } from './types';

/** Module-level cache for config definitions (same 13 rows for all domains). */
let cachedDefinitions: ConfigDefinition[] | null = null;
let definitionsCacheTime = 0;
const DEFINITIONS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface DomainResolution {
    domain: Domain;
    matchStrategy: DomainMatchStrategy;
    /** Whether the matched domain's client is enabled. */
    clientEnabled: boolean;
}

/** A matched domain row as returned by the get_widget_config RPC. */
interface WidgetConfigDomainRow {
    domain: string;
    client_id: string;
    client_enabled: boolean;
    is_alias: boolean;
    config_domain?: string;
    workspace_id?: string;
    workspace_unavailable?: boolean;
    webmcp_compare_enabled?: boolean;
    force_disclaimer?: boolean;
    /** Absent from functions older than IX-5784; null when no experiment runs. */
    staff_experiment?: ActiveStaffExperiment | null;
    domain_experiment?: ActiveDomainExperiment | null;
}

/** Shape of the JSONB payload returned by public.get_widget_config. */
interface WidgetConfigBundle {
    domains: WidgetConfigDomainRow[];
    client_configs: ClientConfigRow[];
}

/**
 * Fetch the anon-safe widget config bundle for a hostname.
 *
 * The anon widget no longer reads public.domains / public.clients /
 * config.client_configs directly (those are now authenticated-only). Instead it
 * calls the scoped get_widget_config RPC, which returns only the rows matching
 * the supplied host candidates — no enumeration, no cross-tenant dump.
 */
async function fetchWidgetConfigBundle(
    rawDomain: string,
    path = '/',
    environment?: string,
    previewToken?: string | null
): Promise<WidgetConfigBundle | null> {
    const candidates = getDomainCandidates(rawDomain);
    if (candidates.length === 0) return null;

    // The backoffice Playground previews routes whose display is off. The RPC
    // authorizes the session for the domain; the public widget never sends one.
    // Without an environment the RPC resolves against production, as deployed widgets do.
    const { data, error } = previewToken
        ? await getSupabase()
              .rpc('get_widget_config_preview', {
                  p_candidates: candidates,
                  p_path: path,
                  ...(environment ? { p_environment: environment } : {}),
              })
              .setHeader('Authorization', `Bearer ${previewToken}`)
        : // get: true issues the RPC as a GET so the api proxy routes it to the
          // read-replica load balancer (SUPABASE_PROXY_URL). The function is STABLE,
          // so it is replica-safe; a POST would land on a read-only replica and fail.
          await getSupabase().rpc(
              'get_widget_config',
              environment
                  ? { p_candidates: candidates, p_path: path, p_environment: environment }
                  : { p_candidates: candidates, p_path: path },
              { get: true }
          );

    if (error) throw error;

    // The RPC returns opaque jsonb (typed as Json), so shape it here. The
    // Array.isArray guard below validates the structure at runtime.
    const bundle = data as WidgetConfigBundle | null;
    if (!bundle || !Array.isArray(bundle.domains) || bundle.domains.length === 0) {
        return null;
    }
    return bundle;
}

/** Fully-resolved widget config for a hostname (single RPC round-trip). */
export interface WidgetConfigResolution {
    domain: Domain;
    matchStrategy: DomainMatchStrategy;
    client: Client;
    clientConfigs: ClientConfigRow[];
    workspaceId?: string;
    workspaceUnavailable?: boolean;
    /** Undefined when the RPC predates IX-5784: fetch it from the experiment RPC. */
    staffExperiment?: ActiveStaffExperiment | null;
    domainExperiment?: ActiveDomainExperiment | null;
}

/**
 * Resolve domain match, client-enabled state, and per-domain config overrides
 * in a single RPC call. Used by ConfigProvider (the widget hot path) so the
 * anon widget makes one request instead of separate domain/client/config reads.
 */
export async function fetchWidgetConfig(
    rawDomain: string,
    path = '/',
    environment?: string,
    previewToken?: string | null
): Promise<WidgetConfigResolution | null> {
    const bundle = await fetchWidgetConfigBundle(rawDomain, path, environment, previewToken);
    if (!bundle) return null;

    const rows: Domain[] = bundle.domains.map((d) => ({
        domain: d.domain,
        client_id: d.client_id,
        is_alias: d.is_alias ?? false,
        workspace_id: d.workspace_id,
        config_domain: d.config_domain,
        webmcp_compare_enabled: d.webmcp_compare_enabled === true,
        force_disclaimer: d.force_disclaimer === true,
    }));
    const match = selectDomainMatch(rawDomain, rows);
    if (!match) return null;

    const matchedRow = bundle.domains.find((d) => d.domain === match.domain.domain);
    if (!matchedRow) return null;

    // The widget only reads client.enabled; name is intentionally not exposed
    // by the anon RPC, so it is left blank.
    const client: Client = {
        id: matchedRow.client_id,
        name: '',
        enabled: matchedRow.client_enabled,
    };
    const clientConfigs = Array.isArray(bundle.client_configs)
        ? bundle.client_configs.filter((cc) => cc.domain === match.domain.domain)
        : [];

    return {
        domain: match.domain,
        matchStrategy: match.matchStrategy,
        client,
        clientConfigs,
        workspaceId: matchedRow.workspace_id,
        workspaceUnavailable: matchedRow.workspace_unavailable,
        staffExperiment: matchedRow.staff_experiment,
        domainExperiment: matchedRow.domain_experiment,
    };
}

/** Resolve the best-matching domain row (exact host first, root fallback). */
export async function fetchDomain(rawDomain: string): Promise<DomainResolution | null> {
    const resolved = await fetchWidgetConfig(rawDomain);
    if (!resolved) return null;
    return {
        domain: resolved.domain,
        matchStrategy: resolved.matchStrategy,
        clientEnabled: resolved.client.enabled,
    };
}

/** Fetch all config definitions from config.configs. Cached module-level. */
export async function fetchConfigDefinitions(): Promise<ConfigDefinition[]> {
    // Return cache if fresh
    if (cachedDefinitions && Date.now() - definitionsCacheTime < DEFINITIONS_CACHE_TTL) {
        return cachedDefinitions;
    }

    const { data, error } = await getSupabase()
        .schema('config')
        .from('configs')
        .select(
            'slug, scope, name, description, category, is_feature, enabled_by_default, requires, default_config, merge_strategy'
        );

    if (error) throw error;

    cachedDefinitions = (data ?? []) as ConfigDefinition[];
    definitionsCacheTime = Date.now();

    logger.debug(`Fetched ${cachedDefinitions.length} config definitions`);
    return cachedDefinitions;
}

/** Fetch per-domain client config overrides via the scoped get_widget_config RPC. */
export async function fetchClientConfigs(domain: string): Promise<ClientConfigRow[]> {
    const bundle = await fetchWidgetConfigBundle(domain);
    if (!bundle || !Array.isArray(bundle.client_configs)) return [];
    return bundle.client_configs.filter((cc) => cc.domain === domain);
}

// Transient network blips on the experiment RPCs can misbucket visitors if
// treated as "no active experiment". Retry locally, then fail the config load so
// the widget does not silently route a control visitor to treatment.
const EXPERIMENT_FETCH_RETRIES = 2;
const EXPERIMENT_FETCH_RETRY_DELAY_MS = 300;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Fetch the single running staff experiment (or null). Anon-readable RPC.
 * Return shape mirrors the legacy `staff_holdout` config so downstream
 * widget bucketing in `experimentCohorts.ts` is untouched.
 */
export async function fetchActiveStaffExperiment(
    domain?: string | null
): Promise<ActiveStaffExperiment | null> {
    for (let attempt = 0; attempt <= EXPERIMENT_FETCH_RETRIES; attempt++) {
        const { data, error } = await getSupabase().rpc(
            'get_active_staff_experiment',
            { p_domain: domain ?? undefined },
            { get: true }
        );
        if (!error) {
            const rows = data as ActiveStaffExperiment[] | null;
            return rows && rows.length > 0 ? (rows[0] ?? null) : null;
        }
        if (isSupabaseRequestCancelled(error)) {
            logger.debug(
                'fetchActiveStaffExperiment cancelled by navigation; aborting experiment resolution'
            );
            throw error;
        }
        if (attempt < EXPERIMENT_FETCH_RETRIES) {
            await sleep(EXPERIMENT_FETCH_RETRY_DELAY_MS);
            continue;
        }
        logger.debug(
            'fetchActiveStaffExperiment failed after retries; aborting experiment resolution',
            {
                domain: domain ?? null,
                attempts: attempt + 1,
                error_code: error.code ?? null,
                error_message: error.message ?? null,
                error_details: error.details ?? null,
                error_hint: error.hint ?? null,
            }
        );
        throw error;
    }
    return null;
}

/**
 * Fetch the single running experiment for a domain (or null). Anon-readable.
 * Return shape mirrors the legacy `client_experiment` config.
 */
export async function fetchActiveDomainExperiment(
    domain: string
): Promise<ActiveDomainExperiment | null> {
    for (let attempt = 0; attempt <= EXPERIMENT_FETCH_RETRIES; attempt++) {
        const { data, error } = await getSupabase().rpc(
            'get_active_domain_experiment',
            { p_domain: domain },
            { get: true }
        );
        if (!error) {
            const rows = data as ActiveDomainExperiment[] | null;
            return rows && rows.length > 0 ? (rows[0] ?? null) : null;
        }
        if (isSupabaseRequestCancelled(error)) {
            logger.debug(
                'fetchActiveDomainExperiment cancelled by navigation; aborting experiment resolution',
                { domain }
            );
            throw error;
        }
        if (attempt < EXPERIMENT_FETCH_RETRIES) {
            await sleep(EXPERIMENT_FETCH_RETRY_DELAY_MS);
            continue;
        }
        logger.debug(
            'fetchActiveDomainExperiment failed after retries; aborting experiment resolution',
            {
                domain,
                attempts: attempt + 1,
                error_code: error.code ?? null,
                error_message: error.message ?? null,
                error_details: error.details ?? null,
                error_hint: error.hint ?? null,
            }
        );
        throw error;
    }
    return null;
}
